// src/components/WebhookHelpModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';

// Plataformas: Cakto / Hotmart / Kiwify / Zouti
const PROVEDORES = {
  cakto:   { id: 'cakto',   label: 'Cakto',   slug: 'cakto' },
  hotmart:{ id: 'hotmart', label: 'Hotmart', slug: 'hotmart' },
  kiwify: { id: 'kiwify',  label: 'Kiwify',  slug: 'kiwify' },
  zouti:  { id: 'zouti',   label: 'Zouti',   slug: 'zouti' },
};

export default function WebhookHelpModal({ open, onClose, repCode }) {
  const [provedor, setProvedor] = useState('cakto'); // padrão

  const codigo = repCode || 'F5D4-1C36-FD18-6046';
  const slug = PROVEDORES[provedor].slug;

  // 2 casos principais
  const urlDisparador365 = `https://zaptech.pro/api/webhooks/${slug}/${codigo}/disparador/365`;
  const urlKit           = `https://zaptech.pro/api/webhooks/${slug}/${codigo}/kit`;

  const copiar = (texto) => {
    try {
      navigator.clipboard.writeText(texto);
    } catch (e) {
      console.error('Falha ao copiar:', e);
    }
  };

  const UrlField = ({ url }) => (
    <TextField
      fullWidth
      size="small"
      value={url}
      InputProps={{
        readOnly: true,
        sx: { fontFamily: 'Consolas, monospace', fontSize: 13 },
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => copiar(url)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const SectionCard = ({ icon, title, subtitle, color = 'primary', children }) => (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        boxShadow: '0 6px 18px rgba(15,23,42,0.08)',
        borderLeft: 4,
        borderColor: `${color}.main`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {children}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          px: 3,
          pt: 2,
          bgcolor: '#0f172a',
          color: '#e5e7eb',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Webhooks ZapTech
        </Typography>
        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
          Dois links principais para vender licenças
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 3,
          bgcolor: '#f3f4f6',
          maxHeight: '80vh',
        }}
      >
        {/* Plataforma (Cakto / Hotmart / Kiwify / Zouti) */}
        <Box
          sx={{
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Plataforma de venda
            </Typography>
            <Typography variant="body2">
              As URLs abaixo usam a plataforma selecionada.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Código do representante: <b>{codigo}</b>
            </Typography>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={provedor}
            exclusive
            onChange={(_, value) => value && setProvedor(value)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 999,
              boxShadow: '0 1px 4px rgba(15,23,42,0.12)',
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 2.5,
              },
            }}
          >
            {Object.values(PROVEDORES).map((p) => (
              <ToggleButton key={p.id} value={p.id}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* 1) Disparador */}
        <SectionCard
          icon={<FlashOnIcon fontSize="small" />}
          title="Disparador ZapTech"
          subtitle="Licença padrão (ex.: 365 dias)"
          color="primary"
        >
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Caso mais comum: venda do programa ZapTech (Disparador) por <b>365 dias</b>:
          </Typography>

          <Box sx={{ mb: 1.5 }}>
            <UrlField url={urlDisparador365} />
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Regras:
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
            • O número no final da URL é a quantidade de dias. <br />
            • <b>Nunca use menos que 30 dias.</b> Se precisar de outro prazo, troque o{' '}
            <code>365</code> por 30, 90, 180, 730, etc. (sempre{' '}
            <code>/disparador/&lt;dias&gt;</code>).
          </Typography>
        </SectionCard>

        {/* 2) Kit */}
        <SectionCard
          icon={<ShieldIcon fontSize="small" />}
          title="Kit Blindagem"
          subtitle="Licença fixa de 30 dias"
          color="success"
        >
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Caso do Kit Blindagem (sempre <b>30 dias</b>):
          </Typography>

          <Box sx={{ mb: 1.5 }}>
            <UrlField url={urlKit} />
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Regra importante: na plataforma de venda, o <b>nome interno</b> do produto precisa ser
            exatamente <code>kit</code>. O webhook já entende que o período é de 30 dias.
          </Typography>
        </SectionCard>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
